<template>
  <div class="questionnaire-template-container">
    <!-- 顶部操作区 -->
    <div class="filter-container">
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="模板名称">
          <el-input v-model="searchForm.name" placeholder="请输入模板名称" clearable @keyup.enter.native="handleSearch" />
        </el-form-item>
        <el-form-item label="适用科室">
          <el-select v-model="searchForm.department" placeholder="请选择科室" clearable>
            <el-option v-for="item in departmentOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="问卷类型">
          <el-select v-model="searchForm.type" placeholder="请选择类型" clearable>
            <el-option v-for="item in typeOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="searchForm.status" placeholder="请选择状态" clearable>
            <el-option v-for="item in statusOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">搜索</el-button>
          <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>

      <div class="operation-container">
        <el-button type="primary" icon="el-icon-plus" @click="handleCreate">新建模板</el-button>
        <el-button type="success" icon="el-icon-document-copy" :disabled="!selectedTemplate" @click="handleCopy">复制</el-button>
        <el-button type="warning" icon="el-icon-edit" :disabled="!selectedTemplate" @click="handleEdit">编辑</el-button>
        <el-button type="danger" icon="el-icon-delete" :disabled="!multipleSelection.length" @click="handleBatchDelete">批量删除</el-button>
        <el-button type="info" icon="el-icon-upload2" @click="handleImport">导入模板</el-button>
        <el-button type="info" icon="el-icon-download" :disabled="!multipleSelection.length" @click="handleExport">导出模板</el-button>
      </div>
    </div>

    <!-- 表格区域 -->
    <el-table
      v-loading="loading"
      :data="pagedTemplates"
      border
      style="width: 100%"
      @selection-change="handleSelectionChange"
      @row-click="handleRowClick"
    >
      <el-table-column type="selection" width="55" />
      <el-table-column prop="id" label="模板ID" width="80" />
      <el-table-column prop="name" label="模板名称" min-width="180">
        <template slot-scope="scope">
          <el-tooltip :content="scope.row.description" placement="top" :disabled="!scope.row.description">
            <span>{{ scope.row.name }}</span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column prop="typeName" label="问卷类型" width="120" />
      <el-table-column prop="departmentName" label="适用科室" width="120" />
      <el-table-column prop="questionCount" label="问题数量" width="90" align="center" />
      <el-table-column prop="creator" label="创建人" width="100" />
      <el-table-column prop="createTime" label="创建时间" width="160" />
      <el-table-column prop="updateTime" label="更新时间" width="160" />
      <el-table-column prop="statusName" label="状态" width="100">
        <template slot-scope="scope">
          <el-tag :type="getStatusType(scope.row.status)">{{ scope.row.statusName }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="220" fixed="right">
        <template slot-scope="scope">
          <el-button size="mini" type="text" icon="el-icon-view" @click.stop="handlePreview(scope.row)">预览</el-button>
          <el-button size="mini" type="text" icon="el-icon-edit" @click.stop="handleEdit(scope.row)">编辑</el-button>
          <el-button size="mini" type="text" icon="el-icon-document-copy" @click.stop="handleCopy(scope.row)">复制</el-button>
          <el-button 
            size="mini" 
            type="text" 
            icon="el-icon-s-promotion" 
            @click.stop="handlePublish(scope.row)"
            v-if="scope.row.status === 'draft'"
          >发布</el-button>
          <el-button 
            size="mini" 
            type="text" 
            icon="el-icon-switch-button" 
            @click.stop="handleDeactivate(scope.row)"
            v-if="scope.row.status === 'published'"
          >停用</el-button>
          <el-button size="mini" type="text" icon="el-icon-delete" @click.stop="handleDelete(scope.row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页器 -->
    <div class="pagination-container">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="filteredTemplates.length"
      />
    </div>

    <!-- 新建/编辑模板对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="templateDialog.visible" width="70%" :before-close="closeTemplateDialog">
      <el-form ref="templateForm" :model="templateForm" :rules="templateRules" label-width="100px">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="模板名称" prop="name">
              <el-input v-model="templateForm.name" placeholder="请输入模板名称" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="问卷类型" prop="type">
              <el-select v-model="templateForm.type" placeholder="请选择问卷类型" style="width: 100%">
                <el-option v-for="item in typeOptions" :key="item.value" :label="item.label" :value="item.value" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="适用科室" prop="department">
              <el-select v-model="templateForm.department" placeholder="请选择适用科室" style="width: 100%">
                <el-option v-for="item in departmentOptions" :key="item.value" :label="item.label" :value="item.value" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="适用疾病" prop="disease">
              <el-select v-model="templateForm.disease" placeholder="请选择适用疾病" multiple style="width: 100%">
                <el-option v-for="item in diseaseOptions" :key="item.value" :label="item.label" :value="item.value" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-form-item label="模板说明" prop="description">
          <el-input type="textarea" v-model="templateForm.description" placeholder="请输入模板说明" :rows="3" />
        </el-form-item>
        
        <el-form-item label="问题设计">
          <el-card class="box-card" v-for="(question, index) in templateForm.questions" :key="index" style="margin-bottom: 10px;">
            <div slot="header" class="clearfix">
              <span>问题 {{ index + 1 }}</span>
              <el-button style="float: right; padding: 3px 0" type="text" icon="el-icon-delete" @click="removeQuestion(index)">删除</el-button>
            </div>
            
            <el-form-item label="问题类型">
              <el-select v-model="question.type" placeholder="请选择问题类型" style="width: 100%">
                <el-option label="单选题" value="radio" />
                <el-option label="多选题" value="checkbox" />
                <el-option label="输入框" value="text" />
                <el-option label="数字" value="number" />
                <el-option label="日期" value="date" />
                <el-option label="评分" value="rate" />
              </el-select>
            </el-form-item>
            
            <el-form-item label="问题标题">
              <el-input v-model="question.title" placeholder="请输入问题标题" />
            </el-form-item>
            
            <el-form-item label="是否必填">
              <el-switch v-model="question.required" />
            </el-form-item>
            
            <template v-if="question.type === 'radio' || question.type === 'checkbox'">
              <el-form-item label="选项">
                <div v-for="(option, optIndex) in question.options" :key="optIndex" class="option-item">
                  <el-input v-model="question.options[optIndex]" placeholder="请输入选项" style="width: 80%;" />
                  <el-button type="text" icon="el-icon-delete" @click="removeOption(question, optIndex)" />
                </div>
                <el-button type="text" icon="el-icon-plus" @click="addOption(question)">添加选项</el-button>
              </el-form-item>
            </template>
            
            <template v-if="question.type === 'rate'">
              <el-form-item label="评分上限">
                <el-input-number v-model="question.maxScore" :min="5" :max="10" />
              </el-form-item>
            </template>
          </el-card>
          
          <div style="margin-top: 10px;">
            <el-button type="primary" plain icon="el-icon-plus" @click="addQuestion">添加问题</el-button>
          </div>
        </el-form-item>
      </el-form>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="closeTemplateDialog">取 消</el-button>
        <el-button type="primary" @click="saveTemplate">保 存</el-button>
      </div>
    </el-dialog>

    <!-- 预览问卷对话框 -->
    <el-dialog title="问卷预览" :visible.sync="previewDialog.visible" width="60%" append-to-body>
      <div v-if="previewTemplate" class="preview-container">
        <h2 class="preview-title">{{ previewTemplate.name }}</h2>
        <p class="preview-description">{{ previewTemplate.description }}</p>
        
        <el-divider></el-divider>
        
        <div v-for="(question, index) in previewTemplate.questions" :key="index" class="preview-question">
          <div class="question-title">
            <span class="question-number">{{ index + 1 }}.</span>
            {{ question.title }}
            <span class="required" v-if="question.required">*</span>
          </div>
          
          <div class="question-content">
            <!-- 单选 -->
            <template v-if="question.type === 'radio'">
              <el-radio-group v-model="previewAnswers[index]">
                <div v-for="(option, optIndex) in question.options" :key="optIndex">
                  <el-radio :label="optIndex">{{ option }}</el-radio>
                </div>
              </el-radio-group>
            </template>
            
            <!-- 多选 -->
            <template v-else-if="question.type === 'checkbox'">
              <el-checkbox-group v-model="previewAnswers[index]">
                <div v-for="(option, optIndex) in question.options" :key="optIndex">
                  <el-checkbox :label="optIndex">{{ option }}</el-checkbox>
                </div>
              </el-checkbox-group>
            </template>
            
            <!-- 文本输入 -->
            <template v-else-if="question.type === 'text'">
              <el-input type="textarea" :rows="2" placeholder="请输入" />
            </template>
            
            <!-- 数字输入 -->
            <template v-else-if="question.type === 'number'">
              <el-input-number :min="0" :step="1" />
            </template>
            
            <!-- 日期选择 -->
            <template v-else-if="question.type === 'date'">
              <el-date-picker type="date" placeholder="选择日期" style="width: 100%" />
            </template>
            
            <!-- 评分 -->
            <template v-else-if="question.type === 'rate'">
              <el-rate :max="question.maxScore || 5" />
            </template>
          </div>
        </div>
      </div>
      
      <div slot="footer" class="dialog-footer">
        <el-button type="primary" @click="previewDialog.visible = false">关闭预览</el-button>
      </div>
    </el-dialog>
    
    <!-- 导入模板对话框 -->
    <el-dialog title="导入问卷模板" :visible.sync="importDialog.visible" width="400px">
      <el-upload
        class="upload-demo"
        drag
        action="#"
        :auto-upload="false"
        :on-change="handleFileChange"
        :limit="1"
        :file-list="importDialog.fileList"
      >
        <i class="el-icon-upload"></i>
        <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
        <div class="el-upload__tip" slot="tip">只能上传json文件，且不超过500kb</div>
      </el-upload>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="importDialog.visible = false">取 消</el-button>
        <el-button type="primary" @click="confirmImport">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: "QuestionnaireTtemplate",
  data() {
    return {
      // 搜索表单
      searchForm: {
        name: "",
        department: "",
        type: "",
        status: ""
      },
      
      // 问卷模板列表
      templateList: [],
      filteredTemplates: [],
      pagedTemplates: [],
      loading: false,
      
      // 选中项
      selectedTemplate: null,
      multipleSelection: [],
      
      // 分页
      pagination: {
        currentPage: 1,
        pageSize: 10
      },
      
      // 新建/编辑对话框
      templateDialog: {
        visible: false,
        type: "create" // create, edit
      },
      templateForm: {
        id: "",
        name: "",
        type: "",
        department: "",
        disease: [],
        description: "",
        questions: []
      },
      templateRules: {
        name: [{ required: true, message: "请输入模板名称", trigger: "blur" }],
        type: [{ required: true, message: "请选择问卷类型", trigger: "change" }],
        department: [{ required: true, message: "请选择适用科室", trigger: "change" }]
      },
      
      // 预览对话框
      previewDialog: {
        visible: false
      },
      previewTemplate: null,
      previewAnswers: {},
      
      // 导入对话框
      importDialog: {
        visible: false,
        fileList: []
      },
      
      // 选项数据
      departmentOptions: [
        { value: "oncology", label: "肿瘤科" },
        { value: "cardiology", label: "心血管内科" },
        { value: "neurology", label: "神经内科" },
        { value: "respiratory", label: "呼吸内科" },
        { value: "gastroenterology", label: "消化内科" },
        { value: "nephrology", label: "肾内科" },
        { value: "endocrinology", label: "内分泌科" },
        { value: "all", label: "全院通用" }
      ],
      typeOptions: [
        { value: "satisfaction", label: "满意度调查" },
        { value: "followup", label: "随访问卷" },
        { value: "screening", label: "筛查问卷" },
        { value: "evaluation", label: "评估量表" },
        { value: "registration", label: "登记表" },
        { value: "feedback", label: "意见反馈" }
      ],
      statusOptions: [
        { value: "draft", label: "草稿" },
        { value: "published", label: "已发布" },
        { value: "inactive", label: "已停用" }
      ],
      diseaseOptions: [
        { value: "lung_cancer", label: "肺癌" },
        { value: "liver_cancer", label: "肝癌" },
        { value: "breast_cancer", label: "乳腺癌" },
        { value: "colorectal_cancer", label: "结直肠癌" },
        { value: "gastric_cancer", label: "胃癌" },
        { value: "esophageal_cancer", label: "食道癌" },
        { value: "prostate_cancer", label: "前列腺癌" },
        { value: "hypertension", label: "高血压" },
        { value: "diabetes", label: "糖尿病" },
        { value: "coronary_heart_disease", label: "冠心病" },
        { value: "stroke", label: "脑卒中" },
        { value: "copd", label: "慢阻肺" }
      ]
    };
  },
  computed: {
    dialogTitle() {
      return this.templateDialog.type === "create" ? "新建问卷模板" : "编辑问卷模板";
    }
  },
  created() {
    this.fetchTemplateData();
  },
  methods: {
    // 获取模板数据
    fetchTemplateData() {
      this.loading = true;
      
      // 模拟API请求
      setTimeout(() => {
        // 模拟数据
        this.templateList = [
          {
            id: "QT001",
            name: "肿瘤患者生活质量评估问卷",
            type: "evaluation",
            typeName: "评估量表",
            department: "oncology",
            departmentName: "肿瘤科",
            disease: ["lung_cancer", "colorectal_cancer"],
            description: "用于评估肿瘤患者的生活质量和日常活动能力",
            questionCount: 15,
            creator: "王医生",
            createTime: "2023-03-15 10:30:45",
            updateTime: "2023-04-10 15:22:18",
            status: "published",
            statusName: "已发布",
            questions: [
              {
                type: "radio",
                title: "您目前的整体健康状况如何?",
                required: true,
                options: ["非常好", "良好", "一般", "较差", "非常差"]
              },
              {
                type: "checkbox",
                title: "您最近一个月内有以下哪些症状? (可多选)",
                required: true,
                options: ["疼痛", "疲劳", "恶心", "呕吐", "食欲不振", "失眠"]
              },
              {
                type: "number",
                title: "请评估您的疼痛程度 (0-10分，0表示无疼痛，10表示极度疼痛)",
                required: true
              }
            ]
          },
          {
            id: "QT002",
            name: "随访满意度调查问卷",
            type: "satisfaction",
            typeName: "满意度调查",
            department: "all",
            departmentName: "全院通用",
            disease: [],
            description: "了解患者对随访服务的满意度和建议",
            questionCount: 10,
            creator: "张护士长",
            createTime: "2023-02-20 09:15:32",
            updateTime: "2023-02-20 09:15:32",
            status: "published",
            statusName: "已发布",
            questions: [
              {
                type: "rate",
                title: "您对本次随访服务的整体满意度",
                required: true,
                maxScore: 5
              },
              {
                type: "radio",
                title: "医护人员的态度如何?",
                required: true,
                options: ["非常满意", "满意", "一般", "不满意", "非常不满意"]
              },
              {
                type: "text",
                title: "您对我们的随访服务有什么建议?",
                required: false
              }
            ]
          },
          {
            id: "QT003",
            name: "糖尿病患者随访问卷",
            type: "followup",
            typeName: "随访问卷",
            department: "endocrinology",
            departmentName: "内分泌科",
            disease: ["diabetes"],
            description: "用于糖尿病患者定期随访的信息收集",
            questionCount: 12,
            creator: "李主任",
            createTime: "2023-01-10 14:20:15",
            updateTime: "2023-03-22 16:40:11",
            status: "published",
            statusName: "已发布",
            questions: [
              {
                type: "number",
                title: "您最近一次测量的空腹血糖值是多少? (mmol/L)",
                required: true
              },
              {
                type: "checkbox",
                title: "您目前有以下哪些症状? (可多选)",
                required: true,
                options: ["口渴", "多尿", "视力模糊", "疲劳", "体重下降", "无以上症状"]
              }
            ]
          },
          {
            id: "QT004",
            name: "高血压健康管理随访表",
            type: "followup",
            typeName: "随访问卷",
            department: "cardiology",
            departmentName: "心血管内科",
            disease: ["hypertension"],
            description: "高血压患者定期随访评估表",
            questionCount: 8,
            creator: "赵医生",
            createTime: "2023-04-02 11:35:28",
            updateTime: "2023-04-02 11:35:28",
            status: "draft",
            statusName: "草稿",
            questions: []
          },
          {
            id: "QT005",
            name: "肺癌术后康复进展调查",
            type: "evaluation",
            typeName: "评估量表",
            department: "oncology",
            departmentName: "肿瘤科",
            disease: ["lung_cancer"],
            description: "评估肺癌患者术后康复情况和生活质量",
            questionCount: 15,
            creator: "刘医生",
            createTime: "2022-12-15 09:30:42",
            updateTime: "2023-01-20 14:25:36",
            status: "inactive",
            statusName: "已停用",
            questions: []
          }
        ];
        
        this.updateFilteredTemplates();
        this.loading = false;
      }, 500);
    },
    
    // 更新筛选后的模板列表
    updateFilteredTemplates() {
      // 应用筛选条件
      let result = [...this.templateList];
      
      if (this.searchForm.name) {
        result = result.filter(t => t.name.toLowerCase().includes(this.searchForm.name.toLowerCase()));
      }
      
      if (this.searchForm.department) {
        result = result.filter(t => t.department === this.searchForm.department);
      }
      
      if (this.searchForm.type) {
        result = result.filter(t => t.type === this.searchForm.type);
      }
      
      if (this.searchForm.status) {
        result = result.filter(t => t.status === this.searchForm.status);
      }
      
      this.filteredTemplates = result;
      
      // 更新分页数据
      this.updatePagedData();
    },
    
    // 更新分页数据
    updatePagedData() {
      const start = (this.pagination.currentPage - 1) * this.pagination.pageSize;
      const end = start + this.pagination.pageSize;
      this.pagedTemplates = this.filteredTemplates.slice(start, end);
    },
    
    // 搜索
    handleSearch() {
      this.pagination.currentPage = 1;
      this.updateFilteredTemplates();
    },
    
    // 重置搜索
    resetSearch() {
      this.searchForm = {
        name: "",
        department: "",
        type: "",
        status: ""
      };
      this.handleSearch();
    },
    
    // 选择行
    handleRowClick(row) {
      this.selectedTemplate = row;
    },
    
    // 表格选择变更
    handleSelectionChange(selection) {
      this.multipleSelection = selection;
    },
    
    // 分页大小变更
    handleSizeChange(val) {
      this.pagination.pageSize = val;
      this.updatePagedData();
    },
    
    // 当前页变更
    handleCurrentChange(val) {
      this.pagination.currentPage = val;
      this.updatePagedData();
    },
    
    // 获取状态标签类型
    getStatusType(status) {
      switch (status) {
        case "published":
          return "success";
        case "draft":
          return "info";
        case "inactive":
          return "danger";
        default:
          return "info";
      }
    },
    
    // 新建模板
    handleCreate() {
      this.templateDialog.type = "create";
      this.templateForm = {
        id: "",
        name: "",
        type: "",
        department: "",
        disease: [],
        description: "",
        questions: []
      };
      this.templateDialog.visible = true;
    },
    
    // 编辑模板
    handleEdit(row) {
      this.templateDialog.type = "edit";
      const template = row || this.selectedTemplate;
      
      if (!template) {
        this.$message.warning("请先选择一个模板");
        return;
      }
      
      // 深拷贝模板数据
      this.templateForm = JSON.parse(JSON.stringify(template));
      this.templateDialog.visible = true;
    },
    
    // 复制模板
    handleCopy(row) {
      const template = row || this.selectedTemplate;
      
      if (!template) {
        this.$message.warning("请先选择一个模板");
        return;
      }
      
      // 深拷贝并修改必要字段
      const copy = JSON.parse(JSON.stringify(template));
      copy.id = "";
      copy.name = `${copy.name} (副本)`;
      copy.status = "draft";
      copy.statusName = "草稿";
      copy.createTime = new Date().toLocaleString();
      copy.updateTime = new Date().toLocaleString();
      
      this.templateDialog.type = "create";
      this.templateForm = copy;
      this.templateDialog.visible = true;
    },
    
    // 预览模板
    handlePreview(row) {
      const template = row || this.selectedTemplate;
      
      if (!template) {
        this.$message.warning("请先选择一个模板");
        return;
      }
      
      // 初始化预览数据
      this.previewTemplate = JSON.parse(JSON.stringify(template));
      this.previewAnswers = {};
      this.previewDialog.visible = true;
    },
    
    // 删除模板
    handleDelete(row) {
      const template = row || this.selectedTemplate;
      
      if (!template) {
        this.$message.warning("请先选择一个模板");
        return;
      }
      
      this.$confirm(`确定要删除问卷模板"${template.name}"吗?`, "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning"
      }).then(() => {
        // 模拟删除操作
        const index = this.templateList.findIndex(t => t.id === template.id);
        if (index !== -1) {
          this.templateList.splice(index, 1);
          this.updateFilteredTemplates();
          this.$message.success("删除成功");
          
          if (this.selectedTemplate && this.selectedTemplate.id === template.id) {
            this.selectedTemplate = null;
          }
        }
      }).catch(() => {});
    },
    
    // 批量删除
    handleBatchDelete() {
      if (this.multipleSelection.length === 0) {
        this.$message.warning("请至少选择一个模板");
        return;
      }
      
      this.$confirm(`确定要删除选中的${this.multipleSelection.length}个模板吗?`, "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning"
      }).then(() => {
        // 模拟批量删除操作
        const ids = this.multipleSelection.map(item => item.id);
        this.templateList = this.templateList.filter(t => !ids.includes(t.id));
        this.updateFilteredTemplates();
        this.$message.success("批量删除成功");
        this.selectedTemplate = null;
      }).catch(() => {});
    },
    
    // 发布模板
    handlePublish(row) {
      const template = row || this.selectedTemplate;
      
      if (!template) {
        this.$message.warning("请先选择一个模板");
        return;
      }
      
      // 检查问题数量
      if (template.questions.length === 0) {
        this.$message.warning("模板中没有包含任何问题，无法发布");
        return;
      }
      
      this.$confirm(`确定要发布问卷模板"${template.name}"吗?`, "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "info"
      }).then(() => {
        // 模拟发布操作
        const index = this.templateList.findIndex(t => t.id === template.id);
        if (index !== -1) {
          this.templateList[index].status = "published";
          this.templateList[index].statusName = "已发布";
          this.templateList[index].updateTime = new Date().toLocaleString();
          this.updateFilteredTemplates();
          this.$message.success("发布成功");
        }
      }).catch(() => {});
    },
    
    // 停用模板
    handleDeactivate(row) {
      const template = row || this.selectedTemplate;
      
      if (!template) {
        this.$message.warning("请先选择一个模板");
        return;
      }
      
      this.$confirm(`确定要停用问卷模板"${template.name}"吗?`, "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning"
      }).then(() => {
        // 模拟停用操作
        const index = this.templateList.findIndex(t => t.id === template.id);
        if (index !== -1) {
          this.templateList[index].status = "inactive";
          this.templateList[index].statusName = "已停用";
          this.templateList[index].updateTime = new Date().toLocaleString();
          this.updateFilteredTemplates();
          this.$message.success("停用成功");
        }
      }).catch(() => {});
    },
    
    // 导入模板
    handleImport() {
      this.importDialog.fileList = [];
      this.importDialog.visible = true;
    },
    
    // 文件变更
    handleFileChange(file) {
      this.importDialog.fileList = [file];
    },
    
    // 确认导入
    confirmImport() {
      if (this.importDialog.fileList.length === 0) {
        this.$message.warning("请先选择要导入的文件");
        return;
      }
      
      // 模拟导入成功
      this.$message.success("导入成功");
      this.importDialog.visible = false;
      
      // 刷新数据
      this.fetchTemplateData();
    },
    
    // 导出模板
    handleExport() {
      if (this.multipleSelection.length === 0) {
        this.$message.warning("请至少选择一个模板");
        return;
      }
      
      this.$message.success(`已导出${this.multipleSelection.length}个问卷模板`);
    },
    
    // 关闭模板对话框
    closeTemplateDialog() {
      this.$confirm("确定要关闭吗？未保存的数据将丢失", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning"
      }).then(() => {
        this.templateDialog.visible = false;
      }).catch(() => {});
    },
    
    // 保存模板
    saveTemplate() {
      this.$refs.templateForm.validate(valid => {
        if (valid) {
          // 模拟保存
          if (this.templateDialog.type === "create") {
            // 创建新模板
            const newTemplate = {
              ...this.templateForm,
              id: `QT${String(this.templateList.length + 1).padStart(3, "0")}`,
              questionCount: this.templateForm.questions.length,
              status: "draft",
              statusName: "草稿",
              creator: "当前用户",
              createTime: new Date().toLocaleString(),
              updateTime: new Date().toLocaleString(),
              typeName: this.typeOptions.find(t => t.value === this.templateForm.type)?.label || "",
              departmentName: this.departmentOptions.find(d => d.value === this.templateForm.department)?.label || ""
            };
            
            this.templateList.unshift(newTemplate);
            this.$message.success("创建成功");
          } else {
            // 更新模板
            const index = this.templateList.findIndex(t => t.id === this.templateForm.id);
            if (index !== -1) {
              this.templateList[index] = {
                ...this.templateList[index],
                ...this.templateForm,
                questionCount: this.templateForm.questions.length,
                updateTime: new Date().toLocaleString(),
                typeName: this.typeOptions.find(t => t.value === this.templateForm.type)?.label || "",
                departmentName: this.departmentOptions.find(d => d.value === this.templateForm.department)?.label || ""
              };
              this.$message.success("更新成功");
            }
          }
          
          this.updateFilteredTemplates();
          this.templateDialog.visible = false;
        } else {
          return false;
        }
      });
    },
    
    // 添加问题
    addQuestion() {
      this.templateForm.questions.push({
        type: "radio",
        title: "",
        required: true,
        options: ["选项1", "选项2", "选项3"]
      });
    },
    
    // 删除问题
    removeQuestion(index) {
      this.templateForm.questions.splice(index, 1);
    },
    
    // 添加选项
    addOption(question) {
      question.options.push(`选项${question.options.length + 1}`);
    },
    
    // 删除选项
    removeOption(question, index) {
      if (question.options.length > 1) {
        question.options.splice(index, 1);
      } else {
        this.$message.warning("至少需要保留一个选项");
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.questionnaire-template-container {
  padding: 20px;
  
  .filter-container {
    background-color: #fff;
    padding: 20px;
    margin-bottom: 20px;
    border-radius: 4px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    
    .search-form {
      margin-bottom: 20px;
    }
    
    .operation-container {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
    }
  }
  
  .pagination-container {
    background-color: #fff;
    padding: 20px;
    margin-top: 20px;
    border-radius: 4px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    display: flex;
    justify-content: flex-end;
  }
  
  .option-item {
    display: flex;
    align-items: center;
    margin-bottom: 5px;
  }
  
  .preview-container {
    padding: 20px;
    
    .preview-title {
      font-size: 20px;
      font-weight: bold;
      text-align: center;
      margin-bottom: 10px;
    }
    
    .preview-description {
      color: #666;
      text-align: center;
      margin-bottom: 20px;
    }
    
    .preview-question {
      margin-bottom: 25px;
      
      .question-title {
        font-size: 16px;
        font-weight: bold;
        margin-bottom: 10px;
        
        .question-number {
          margin-right: 5px;
        }
        
        .required {
          color: #f56c6c;
          margin-left: 5px;
        }
      }
      
      .question-content {
        padding-left: 25px;
      }
    }
  }
}
</style>